import React, { useState } from "react";
import SectionLabel from "../../components/SectionLabel";
import Ticket from "../../components/Ticket";
import RouteStrip from "../../components/RouteStrip";
import MiniStat from "../../components/MiniStat";
import DocumentsPanel from "./DocumentsPanel";
import ArgumentsPanel from "./ArgumentsPanel";
import { formatDate, daysUntil } from "../../lib/format";
import { BR_TRAVEL_AGENCIES } from "../../lib/brazil-aviation";
import "./CaseShow.css";

const STATUS_LABELS = {
  collecting_docs: "Coletando documentos",
  generating_arguments: "Gerando argumentos",
  drafting_letters: "Montando cartas",
  sent: "Enviado",
  replied: "Respondido",
  escalated: "Escalado",
  resolved: "Resolvido",
  archived: "Arquivado",
};

const AGENCY_LABELS = Object.fromEntries(BR_TRAVEL_AGENCIES.map((a) => [a.slug, a.name]));

export default function CaseShow({ case: record }) {
  const incident = record.incident ?? {};
  const origin = incident.origin_iata || "—";
  const destination = incident.destination_iata || "—";
  const flight = incident.flight_code || incident.airline_iata || "—";
  const deadlineDays = daysUntil(record.airline_deadline_at);
  const documents = record.documents ?? [];
  const argumentsList = record.arguments ?? [];
  const isClosed = Boolean(record.closed);
  const [activeTab, setActiveTab] = useState("arguments");

  return (
    <main className={`trunfo-case${isClosed ? " trunfo-case--closed" : ""}`}>
      {isClosed ? (
        <aside className="trunfo-case__closed-banner" role="status">
          <SectionLabel tone="accent">Caso concluído</SectionLabel>
          <p>
            Este caso foi marcado como concluído. As ações de geração e revisão da carta estão
            bloqueadas.
          </p>
        </aside>
      ) : null}
      <header className="trunfo-case__header">
        <div className="trunfo-case__header-main">
          <SectionLabel tone="accent">Caso {record.public_code}</SectionLabel>
          <h1 className="trunfo-case__title">{record.title}</h1>
          <p className="trunfo-case__lede">
            {record.problem_type} · {record.problem_type_summary}
          </p>
        </div>
        {record.complaint_letter ? (
          <a
            className="trunfo-btn trunfo-btn--primary trunfo-case__letter-link"
            href={record.complaint_letter_path}
          >
            {isClosed ? "Ver carta formal" : "Ver carta formal →"}
          </a>
        ) : null}
      </header>

      <Ticket
        header={
          <>
            <SectionLabel>Status</SectionLabel>
            <SectionLabel tone="accent">
              {STATUS_LABELS[record.status] ?? record.status}
            </SectionLabel>
          </>
        }
        stub={
          deadlineDays !== null ? (
            <>
              <SectionLabel tone="mute">Prazo cia</SectionLabel>
              <SectionLabel tone="accent">
                {deadlineDays === 0 ? "hoje" : `${deadlineDays} dias`}
              </SectionLabel>
            </>
          ) : null
        }
      >
        <RouteStrip
          origin={origin}
          destination={destination}
          flightCode={flight}
          meta={record.problem_type}
        />
      </Ticket>

      <section className="trunfo-case__stats">
        <MiniStat
          label="Aconteceu em"
          value={formatDate(record.happened_at) ?? "—"}
          helper="data do incidente"
        />
        <MiniStat label="Companhia" value={incident.airline_iata || "—"} helper="código IATA" />
        <MiniStat
          label="Onde comprou"
          value={AGENCY_LABELS[incident.travel_agency] ?? incident.travel_agency ?? "—"}
          helper="canal de compra"
        />
      </section>

      {incident.notes ? (
        <section className="trunfo-case__notes">
          <SectionLabel tone="mute">Relato</SectionLabel>
          <p>{incident.notes}</p>
        </section>
      ) : null}

      <nav className="trunfo-case__tabs" role="tablist" aria-label="Seções do caso">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "arguments"}
          className={`trunfo-case__tab ${activeTab === "arguments" ? "trunfo-case__tab--active" : ""}`}
          onClick={() => setActiveTab("arguments")}
        >
          Argumentos <span className="trunfo-case__tab-count">{argumentsList.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === "documents"}
          className={`trunfo-case__tab ${activeTab === "documents" ? "trunfo-case__tab--active" : ""}`}
          onClick={() => setActiveTab("documents")}
        >
          Documentos <span className="trunfo-case__tab-count">{documents.length}</span>
        </button>
      </nav>

      {activeTab === "documents" ? (
        <DocumentsPanel publicCode={record.public_code} documents={documents} />
      ) : (
        <ArgumentsPanel
          buildPath={record.build_arguments_path}
          caseStatus={record.status}
          caseClosed={isClosed}
          argumentsList={argumentsList}
          legalArticles={record.legal_articles ?? []}
          view="all"
        />
      )}
    </main>
  );
}
